import { defineComponent as _defineComponent } from 'vue'
import { PropType, ref, Ref, onMounted, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ControlPosition, DraggableEvent } from '@braks/revue-draggable'
import { TRACKING_TYPE } from '@/types/main'
import { playSounds, stopSounds, soundIsPlaying } from '@/utilities'
import { TrackingDetails } from '@/types/main'
import { useGameStore } from '@/store/useGameStore'
import { useUserStore } from '@/store/useUserStore'
import { useDeviceService } from '@/composition/useDevice'
import Button from '@/components/base/Button.vue'
import Celebrate from '@/components/task/horerduordet/Celebrate.vue'
import { HorerDuOrdetSentence, Horerduordet, TASK_TYPES } from '@/templates/slplus/structure/questionModels'

import instructions from '@/assets/tasks/instructions.svg'
import placement from '@/assets/tasks/placement.svg'
//import lightBulb from '@/assets/tasks/sevenseas/Omar-lightball.svg'

import bgTextImage from '@/assets/tasks/sevenseas/Flat_3.svg'
import omarSpeaksImage from '@/assets/omar/OMAR-GLAD.svg'
import listenAgainImage from '@/assets/tasks/horerduordet/listenagain.svg'
import bgTextImageBlue from '@/assets/tasks/sevenseas/Flat_discussiontime_blue.svg'

import hearAgainSound from '@/assets/tasks/horerduordet/740_hørerduordet_hørigjen_alex.mp3'

interface BallStatus {
  index: number
  playerID: string
  active: boolean
  speed: number
  sunk: boolean
  startPos: ControlPosition
  endPos: ControlPosition
}
type Balls = Record<number, Ball>
type Timer = ReturnType<typeof setTimeout>

interface Ball {
  status: BallStatus
  position: ControlPosition
  element?: HTMLElement
}


export default /*#__PURE__*/_defineComponent({
  __name: 'HorerQuizz',
  props: {
  sentencelist: {
    type: Array as PropType<HorerDuOrdetSentence[]>,
    default: () => [],
  },
  question: { required: true, type: Object as PropType<Horerduordet> },
  round: { type: Number, default: 0 },
  bounce: { type: Number, default: 0 },
},
  emits: ['done', 'finish', 'review', 'playsound', 'stopsound'],
  setup(__props, { expose, emit }) {
  expose();

const props = __props

const messages = {
  no: {
    letsDiscuss: 'Diskusjonstid!',
    throwIfYouHear: 'Kast ballen din på meg hvis du hører det hemmelige ordet',
    tip: 'Pass på! Ord kan brukes ulikt',
    bulbDiscussion: 'Snakk sammen',
    bulbTip: 'Tip!',
  },
  en: {
    letsDiscuss: 'Discussion time!',
    throwIfYouHear: 'Throw your ball at me if you hear the secret word',
    tip: 'Be careful! A word can have different tense!',
    bulbDiscussion: 'Snakk sammen',
    bulbTip: 'Tip!',
  },
}




const { t } = useI18n({ messages })
const { sentencelist, question } = toRefs(props)
const gameStore = useGameStore()
const userStore = useUserStore()
const { getters: deviceGetters, actions: deviceActions } = useDeviceService()
const players = gameStore.getters.playersInSelectedGame
const quizzDone = ref(false)
const awaitingNext = ref(false)
const awaitingTipNext = ref(false)
const quizzDoneMode = ref('straight')
const areYouSure = ref(false)
const showSentence = ref(false)
const showAdvice = ref(true)
const bounceAgain = ref(0)
const adviceText = ref(t('tip'))
const bulbText = ref('bulbTip')
const round = ref(props.round)
const sentences: Ref<HorerDuOrdetSentence[]> = ref([])
const currentSentence: Ref<HorerDuOrdetSentence | undefined> = ref()
let ballTimeout: Timer // This gets cleared if more balls are thrown
let bounceTimeout: Timer
let currentTracking: TrackingDetails | undefined
const isIOS = window.cordova && !!window.cordova.platformId
const results: Ref<Record<string, number>> = ref({}) // Number of throws for each player, keyed by player ID
players.value.forEach((p) => (results.value[p._id] = 0))

let initialBallPositions: Record<number, ControlPosition> = {}

const setSizes = (): void => {
  const screenSize = {
    w: document.documentElement.clientWidth,
    h: document.documentElement.clientHeight,
  }
  initialBallPositions = {
    0: {
      x: 31,
      y: 40,
    },
    1: {
      x: screenSize.w - 158,
      y: screenSize.h - (isIOS ? 148 : 167), //148 for iPad
    },
    2: {
      x: 31,
      y: screenSize.h - (isIOS ? 148 : 167), // 148 for iPad
    },
    3: {
      x: screenSize.w - 159,
      y: 39,
    },
  }
}
setSizes()
window.onresize = (): void => {
  setTimeout(() => {
    setSizes()
    resetBalls()
  }, 500)
}

let tipStage = 1

const balls: Ref<Balls> = ref({})

const finalBall0: Ref<HTMLElement | undefined> = ref()
const finalBall1: Ref<HTMLElement | undefined> = ref()
const finalBall2: Ref<HTMLElement | undefined> = ref()
const finalBall3: Ref<HTMLElement | undefined> = ref()

const finalBalls = [finalBall0, finalBall1, finalBall2, finalBall3]

watch(
  () => props.bounce,
  () => {
    clearTimeout(bounceTimeout)
    bounceAgain.value++
    bounceTimeout = setTimeout(() => (bounceAgain.value = 0), 4000)
  },
)

const confirmEndRound = () => {
  quizzDone.value = true
  awaitingNext.value = false
}
const confirmTip = () => {
  tipStage = 2
  stopSounds()
  awaitingTipNext.value = false
  adviceText.value = t('throwIfYouHear')
  bulbText.value = 'bulbTip'
  playSounds([question.value.instructionSound3], activateSentence)
}

const reviewState = (stateName: string) => {
  resetAll()
  emit('review', stateName, round)
}

function resetBalls() {
  players.value.forEach((p, i) => {
    const finalBall = finalBalls[i]
    const pos = finalBall.value ? finalBall.value.getBoundingClientRect() : undefined
    balls.value[i] = {
      status: {
        index: i,
        playerID: p._id,
        active: false,
        speed: 0,
        sunk: false,
        startPos: initialBallPositions[i],
        endPos: {
          x: pos ? pos.x : 0,
          y: pos ? pos.y : 0,
        },
      },
      position: initialBallPositions[i],
    }
  })
}

function resetAll() {
  quizzDone.value = false
  areYouSure.value = false
  currentSentence.value = undefined
  adviceText.value = t('tip')
  bulbText.value = t('bulbTip')
  clearTimeout(ballTimeout)
  setTimeout(() => {
    setSizes()
    resetBalls()
  }, 500) // Screen resize seems to take longer than one tick sometimes
}

const startTracking = async () => {
  await deviceActions.createAudio()
  await deviceActions.startRecordingAudio()
  const game = gameStore.getters.selectedGame.value
  if (game) {
    currentTracking = new TrackingDetails({
      itemID: question.value._id,
      gameID: game._id,
      playerIDs: players.value.map((p) => p._id),
      projectID: game.project.id,
      type: TRACKING_TYPE.subtask,
      details: {
        user: userStore.getters.myUser.value.profile.username,
        taskType: TASK_TYPES.horerduordet,
        taskInfo: question.value.name,
        word: question.value.word,
        isCorrect: true,
      },
    })
  }
}
const stopTracking = () => {
  return deviceActions.stopRecordingAudio().then(() => {
    if (currentTracking) {
      currentTracking.details.results = { throws: { ...results.value }, sentence: sentencelist.value.length - sentences.value.length }
      currentTracking.audioFile = deviceGetters.audioFilename.value
      currentTracking.complete()
      gameStore.actions.commitNewTracking(currentTracking)
      players.value.forEach((p) => (results.value[p._id] = 0))
    }
  })
}

const setupTask = async () => {
  currentTracking = undefined
  await deviceActions.stopRecordingAudio()
  startRound(false)
}

onMounted(() => {
  clearTimeout(bounceTimeout)
  bounceTimeout = setTimeout(() => (bounceAgain.value = 0), 4000)
  bounceAgain.value++
  sentences.value = sentencelist.value.map((w) => w)
  for (let i = 0; i < round.value; i++) {
    sentences.value.shift()
  }
  resetAll()
  if (sentences.value.length > 0) setupTask()
})

function waitForAllBalls(timeout: number) {
  clearTimeout(ballTimeout)
  ballTimeout = setTimeout(() => {
    if (areYouSure.value === true || Object.values(balls.value).every((b) => b.status.sunk)) {
      awaitingNext.value = true
      quizzDoneMode.value = 'zigzag'
    } else if (currentSentence.value && currentSentence.value.wordExists) {
      areYouSure.value = true
      const currentSentenceSound = currentSentence.value ? currentSentence.value.sound : ''
      playSounds([hearAgainSound, currentSentenceSound], () => waitForAllBalls(3000))
    }
  }, timeout)
}

function noBallsNeeded() {
  clearTimeout(ballTimeout)
  ballTimeout = setTimeout(() => {
    awaitingNext.value = true
    quizzDoneMode.value = 'straight'
  }, 5000)
}

const activate = () => {
  const s = currentSentence.value
  if (s && !s.wordExists) noBallsNeeded()
  else waitForAllBalls(5000)
}

const activateSentence = () => {
  showAdvice.value = false
  const s = currentSentence.value
  const onSoundEnd = () => {
    activate()
  }
  setTimeout(() => {
    const sound = s ? s.sound : ''
    showSentence.value = true
    playSounds([sound], onSoundEnd)
  }, 3000)
}

const playSentence = () => {
  stopSounds()
  if (showAdvice.value) {
    if (round.value === 0) {
      if (tipStage === 1) {
        playSounds([question.value.instructionSound2], () => {
          awaitingTipNext.value = true
        })
      } else if (tipStage === 2) {
        playSounds([question.value.instructionSound3], activateSentence)
      }
    }
  } else {
    clearTimeout(ballTimeout)
    const s = currentSentence.value
    const sound = s ? s.sound : ''
    playSounds([sound], activate)
  }
}

const startRound = (restart: boolean) => {
  stopTracking().then(() => {
    resetAll()
    startTracking()
    if (sentences.value.length > 0 || restart) {
      const s = restart ? currentSentence.value : sentences.value.shift()
      if (s) {
        currentSentence.value = s
        // Show "Pas på!" text
        if (round.value === 0) {
          showAdvice.value = true
          showAdvice.value = true
          playSounds([question.value.instructionSound2], () => {
            awaitingTipNext.value = true
          })
        } else activateSentence()
      }
    } else emit('done')
  })
}

const nextRound = () => {
  round.value++
  showSentence.value = false
  startRound(false)
}

function onStart(e: DraggableEvent, ball: Ball) {
  e.event.stopPropagation()
  clearTimeout(ballTimeout)
  const el = e && (e.event.target as HTMLElement)
  ball.element = el
}

function onStop(e: DraggableEvent, ball: Ball) {
  let x = 0
  let y = 0
  if (e.event.type.includes('touch')) {
    const te = e.event as TouchEvent
    x = te.changedTouches[0].clientX
    y = te.changedTouches[0].clientY
  } else {
    const me = e.event as MouseEvent
    x = me.clientX
    y = me.clientY
  }
  const xDiff = x - ball.status.startPos.x
  const yDiff = y - ball.status.startPos.y
  bounceBall(ball, xDiff, yDiff)
  /* if (Math.abs(xDiff) > 10 && Math.abs(yDiff) > 10) {
        bounceBall(ball, xDiff, yDiff)
      } else {
        ball.position = { ...ball.status.startPos }
        waitForAllBalls()
      } */
}

function bounceBall(ball: Ball, xDiff: number, yDiff: number) {
  if (ball.element) ball.element.classList.add('movingBall')
  ball.position = { x: Math.floor(xDiff * 5), y: Math.floor(yDiff * 5) }
  results.value[ball.status.playerID]++
  setTimeout(() => {
    if (currentSentence.value && currentSentence.value.wordExists && !showAdvice.value) {
      ball.position = { ...ball.status.endPos }
      ball.status.sunk = true
    } else ball.position = { ...ball.status.startPos }
    setTimeout(() => {
      if (ball.element) ball.element.classList.remove('movingBall')
      if (!awaitingTipNext.value) {
        if (currentSentence.value && !currentSentence.value.wordExists) noBallsNeeded()
        else waitForAllBalls(5000)
      }
    }, 1000)
  }, 1000)
}

const __returned__ = { messages, emit, props, t, sentencelist, question, gameStore, userStore, deviceGetters, deviceActions, players, quizzDone, awaitingNext, awaitingTipNext, quizzDoneMode, areYouSure, showSentence, showAdvice, bounceAgain, adviceText, bulbText, round, sentences, currentSentence, ballTimeout, bounceTimeout, currentTracking, isIOS, results, initialBallPositions, setSizes, tipStage, balls, finalBall0, finalBall1, finalBall2, finalBall3, finalBalls, confirmEndRound, confirmTip, reviewState, resetBalls, resetAll, startTracking, stopTracking, setupTask, waitForAllBalls, noBallsNeeded, activate, activateSentence, playSentence, startRound, nextRound, onStart, onStop, bounceBall, stopSounds, soundIsPlaying, Button, Celebrate, instructions, placement, bgTextImage, omarSpeaksImage, listenAgainImage, bgTextImageBlue }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})